import { useEffect, useId, useMemo, useRef, useState } from 'react';
import styles from './AgriTourSelect.module.css';

export type AgriTourSelectOption = {
  value: string;
  label: string;
};

export interface AgriTourSelectProps {
  value: string;
  options: AgriTourSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  emptyMessage?: string;
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export default function AgriTourSelect({
  value,
  options,
  onChange,
  placeholder = '— בחר —',
  hasError = false,
  disabled = false,
  searchable = false,
  emptyMessage = 'לא נמצאו תוצאות',
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: AgriTourSelectProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const selected = options.find((o) => o.value === value);

  const visibleOptions = useMemo(() => {
    const q = filter.trim();
    if (!searchable || !q) return options;
    return options.filter((o) => o.label.includes(q));
  }, [filter, options, searchable]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setFilter('');
  };

  return (
    <div
      ref={rootRef}
      className={`${styles.root} ${open ? styles.open : ''}`}
    >
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid ?? (hasError ? true : undefined)}
        aria-describedby={ariaDescribedBy}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        disabled={disabled}
        className={`${styles.trigger} ${hasError ? styles.triggerError : ''} ${!value ? styles.placeholder : ''}`}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
      >
        <span className={styles.triggerLabel}>
          {selected?.label ?? placeholder}
        </span>
        <span className={styles.chevron} aria-hidden />
      </button>

      {open && (
        <div className={styles.menu}>
          {searchable && (
            <input
              type="search"
              className={styles.search}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="חיפוש..."
              aria-label="חיפוש ברשימה"
            />
          )}
          <ul id={listId} className={styles.list} role="listbox" aria-label={ariaLabel}>
            {visibleOptions.length === 0 ? (
              <li className={styles.empty}>
                {options.length === 0 && !filter.trim()
                  ? emptyMessage
                  : 'לא נמצאו תוצאות'}
              </li>
            ) : (
              visibleOptions.map((option) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  className={`${styles.option} ${option.value === value ? styles.optionSelected : ''}`}
                  onClick={() => {
                    onChange(option.value);
                    close();
                  }}
                >
                  {option.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
